<div class="border-default overflow-x-auto rounded-sm border">
  <table class="w-full border-collapse text-xs">
    <thead>
      <tr class="bg-elevated border-weak border-b">
        @if (supportsDeployInSteps()) {
          <th class="w-8 px-2 py-1.5">
            <app-checkbox
              data-testid="deploy-select-all"
              [checked]="allSelected()"
              [indeterminate]="someSelected()"
              [disabled]="noneSelectable()"
              [label]="''"
              [onChange]="() => onToggleAll()" />
          </th>
        }
        <th
          class="text-text-tertiary px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-wider uppercase">
          {{ 'features.plan.deploy.table.uid' | translate }}
        </th>
        <th
          class="text-text-tertiary px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-wider uppercase">
          {{ 'features.plan.deploy.table.label' | translate }}
        </th>
        <th
          class="text-text-tertiary px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-wider uppercase">
          {{ 'features.plan.deploy.table.recipients' | translate }}
        </th>
        <th
          class="text-text-tertiary px-2 py-1.5 text-left text-[10.5px] font-semibold tracking-wider uppercase">
          {{ 'features.plan.deploy.table.state' | translate }}
        </th>
        <th
          class="text-text-tertiary px-2 py-1.5 text-right text-[10.5px] font-semibold tracking-wider uppercase">
          {{ 'features.plan.deploy.table.last_send' | translate }}
        </th>
        <th class="w-10 px-2 py-1.5"></th>
      </tr>
    </thead>

    <tbody>
      @for (row of rows(); track row.message.uid) {
        <!-- An open row and its detail row hover as one: they are one message. -->
        <tr
          data-testid="deploy-message-row"
          [attr.data-uid]="row.message.uid"
          class="border-weak hover:bg-interactive has-[+[data-testid=deploy-message-detail]:hover]:bg-interactive [&:hover+[data-testid=deploy-message-detail]]:bg-interactive transition-colors last:border-b-0"
          [class.border-b]="!(row.hasDetail && isExpanded(row.message.uid))">
          @if (supportsDeployInSteps()) {
            <td class="px-2 py-1.5 align-top">
              <app-checkbox
                [checked]="row.selected"
                [disabled]="row.locked"
                [label]="''"
                [onChange]="() => onToggle(row)" />
            </td>
          }

          <!-- A sent uid is coloured rather than badged alone: the operator scans this column
               to find a route, so "already gone out" belongs where the eye already is. -->
          <td
            class="px-2 py-1.5 align-top font-mono tabular-nums"
            [class.text-success]="row.hasSuccessfulSend">
            {{ row.message.uid }}
          </td>

          <td class="text-text-secondary px-2 py-1.5 align-top">{{ row.message.label }}</td>

          <td class="text-text-secondary px-2 py-1.5 align-top">
            <app-deploy-recipients-cell [recipients]="row.message.recipients" />
          </td>

          <td class="px-2 py-1.5 align-top">
            <div class="flex flex-wrap items-center gap-1">
              @if (row.status; as status) {
                @if (row.warningCount > 0) {
                  <button
                    type="button"
                    data-testid="deploy-warnings-toggle"
                    class="cursor-pointer rounded-full transition-opacity hover:opacity-80"
                    [attr.aria-expanded]="isExpanded(row.message.uid)"
                    [attr.aria-label]="
                      'features.plan.deploy.table.toggle_warnings'
                        | translate: { count: row.warningCount }
                    "
                    (click)="toggleDetail(row.message.uid)">
                    <app-status-pill [variant]="status.variant" [dot]="true">
                      {{ status.key | translate: { count: row.warningCount } }}
                      <i
                        [class]="ICONS.caretDown"
                        class="text-[10px] transition-transform"
                        [class.rotate-180]="isExpanded(row.message.uid)"></i>
                    </app-status-pill>
                  </button>
                } @else {
                  <app-status-pill [variant]="status.variant" [dot]="true">
                    {{ status.key | translate: { count: row.warningCount } }}
                  </app-status-pill>
                }
              }

              @if (row.sending) {
                <app-status-pill
                  data-testid="deploy-sending-badge"
                  [variant]="'info'"
                  [dot]="true">
                  {{ 'features.plan.deploy.table.sending' | translate }}
                </app-status-pill>
              } @else {
                <!-- Both, not one or the other: a message that went out and whose re-send
                       then failed is each of those things, and dropping either half loses
                       the only clue that the two attempts disagree. -->
                @if (row.hasSuccessfulSend) {
                  <app-status-pill
                    data-testid="deploy-sent-badge"
                    [variant]="'success'"
                    [dot]="true">
                    {{ 'features.plan.deploy.table.sent_badge' | translate }}
                  </app-status-pill>
                }

                @if (row.lastFailed) {
                  <!-- The caret lands here only when no warning pill carries it, so a row
                         with both keeps one affordance opening one region. -->
                  @if (row.lastFailure !== null && row.warningCount === 0) {
                    <button
                      type="button"
                      data-testid="deploy-failure-toggle"
                      class="cursor-pointer rounded-full transition-opacity hover:opacity-80"
                      [attr.aria-expanded]="isExpanded(row.message.uid)"
                      [attr.aria-label]="
                        'features.plan.deploy.error_detail_label' | translate
                      "
                      (click)="toggleDetail(row.message.uid)">
                      <app-status-pill
                        data-testid="deploy-failed-badge"
                        [variant]="'danger'"
                        [dot]="true">
                        {{ 'features.plan.deploy.table.failed_badge' | translate }}
                        <i
                          [class]="ICONS.caretDown"
                          class="text-[10px] transition-transform"
                          [class.rotate-180]="isExpanded(row.message.uid)"></i>
                      </app-status-pill>
                    </button>
                  } @else {
                    <app-status-pill
                      data-testid="deploy-failed-badge"
                      [variant]="'danger'"
                      [dot]="true">
                      {{ 'features.plan.deploy.table.failed_badge' | translate }}
                    </app-status-pill>
                  }
                }
              }
            </div>
          </td>

          <td class="px-2 py-1.5 text-right align-top font-mono tabular-nums">
            @if (row.lastSend) {
              <span [class]="row.lastSend.success ? 'text-text-secondary' : 'text-danger'">
                {{ row.lastSend.sentAt | date: 'short' }}
              </span>
              @if (row.externalId) {
                <span class="text-text-tertiary block text-[10.5px]">
                  {{
                    'features.plan.deploy.table.external_id'
                      | translate: { id: row.externalId }
                  }}
                </span>
              }
            } @else {
              <span class="text-text-tertiary">
                {{ 'features.plan.deploy.table.never_sent' | translate }}
              </span>
            }
          </td>

          <td class="px-2 py-1.5 text-right align-top">
            <div class="inline-flex items-center gap-0.5">
              <button
                type="button"
                data-testid="deploy-history-open"
                class="text-text-tertiary hover:text-text-primary hover:bg-interactive disabled:hover:text-text-tertiary inline-flex h-5 w-5 items-center justify-center rounded-sm transition-colors not-disabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                [disabled]="row.message.sends.length === 0"
                [attr.aria-label]="'features.plan.deploy.history.open' | translate"
                (click)="viewHistory.emit(row.message)">
                <i [class]="ICONS.clockCounterClockwise" class="text-xs"></i>
              </button>

              <button
                type="button"
                class="text-text-tertiary hover:text-text-primary hover:bg-interactive inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm transition-colors"
                [attr.aria-label]="'features.plan.deploy.view_content' | translate"
                (click)="viewMessage.emit(row.message)">
                <i [class]="ICONS.eye" class="text-xs"></i>
              </button>
            </div>
          </td>
        </tr>

        <!-- Its own full-width row so a long warning list or transport error never widens the
             state column and reflows every other one. -->
        @if (row.hasDetail && isExpanded(row.message.uid)) {
          <tr
            data-testid="deploy-message-detail"
            class="border-weak bg-elevated hover:bg-interactive border-b transition-colors last:border-b-0">
            <td
              [attr.colspan]="columnCount()"
              class="pt-0.5 pr-2 pb-2"
              [class.pl-10]="supportsDeployInSteps()"
              [class.pl-2]="!supportsDeployInSteps()">
              <div class="flex flex-col gap-1">
                @if (row.warningCount > 0) {
                  <app-deploy-warnings-list
                    [messages]="row.message.warnings"
                    [subjectUid]="row.message.uid"
                    [dense]="true" />
                }

                @if (row.lastFailure; as failure) {
                  <div class="flex flex-col gap-0.5">
                    <span class="text-text-secondary text-[10.5px] font-medium">
                      {{ 'features.plan.deploy.error_detail_label' | translate }}
                    </span>
                    <!-- The same soft-red panel the deploy block gives a channel-level error,
                         so a failure reads the same wherever it is reported. Its own classes
                         rather than `app-deploy-warnings-list`: this is a transport's raw
                         string, not a coded message, and there is nothing to resolve. -->
                    <div
                      class="bg-danger-soft border-danger overflow-hidden rounded-sm border px-2 py-1.5">
                      <!-- `whitespace-pre-wrap`: a channel's failure is whatever the transport
                           said, and a real one runs to several lines of OpenSSL output.
                           `text-text-primary`, not `text-danger`: red on the soft red fill
                           scores about 3:1, and the box already says this is a failure. -->
                      <p
                        data-testid="deploy-failure-detail"
                        class="text-text-primary font-mono text-[10.5px] leading-relaxed break-words whitespace-pre-wrap">
                        {{ failure }}
                      </p>
                    </div>
                  </div>
                }
              </div>
            </td>
          </tr>
        }
      }
    </tbody>
  </table>
</div>
